import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import type { LeadAnalysisItem } from '@/api/types';
import { Callout } from '@/components/Callout';
import { formatWhen } from '@/pages/AccountsPage';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';

const KIND_LABELS: Record<string, string> = {
  daily_digest: 'Daily digest',
  campaigns: 'Campaigns & channels',
  service_quality: 'Response quality',
};

/**
 * AI findings about the leads, read from STORED analyses.
 *
 * This panel never triggers a model call on render. Gemini's free-tier request quota is unpublished
 * and has already been cut sharply, so a page that analyses on load would break on Google's
 * schedule rather than ours — and would spend the day's allowance on whoever happened to refresh.
 * Reading stored rows means a failed run shows yesterday's analysis with its date attached.
 */
export function LeadInsights({ siteKey }: { siteKey: string }) {
  const qc = useQueryClient();
  const [ran, setRan] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['lead-insights', siteKey],
    queryFn: () => api.leads.insights(siteKey),
    retry: false,
  });

  const analyse = useMutation({
    mutationFn: () => api.leads.analyse(siteKey),
    onSuccess: (r) => {
      setRan(
        r.note
          ? r.note
          : `${r.analyses_produced} analyses, ${r.leads_scored} leads scored`,
      );
      qc.invalidateQueries({ queryKey: ['lead-insights', siteKey] });
      // The keys the leads page actually reads (useLeads) — ['leads'] matched no query, so nothing refreshed.
      qc.invalidateQueries({ queryKey: ['leads-summary', siteKey] });
      qc.invalidateQueries({ queryKey: ['leads-list', siteKey] });
    },
    onError: (e) => setRan(e instanceof ApiError ? e.message : 'Analysis failed'),
  });

  if (isLoading) return <div className="empty-state"><span className="spinner" /></div>;

  if (isError) {
    return (
      <Callout tone="error" title="Insights unavailable">
        {error instanceof ApiError ? error.message : 'Unexpected error.'}
      </Callout>
    );
  }

  if (!data) return null;

  // An unset API key is a supported state, not a failure — same contract as GA4 and Clarity here.
  if (!data.is_configured) {
    return (
      <Callout tone="info" title="AI analysis is not configured">
        Set <code>ControlPlane__Ai__ApiKey</code> to a Gemini API key to enable lead analysis.
        Everything else on this page works without it.
      </Callout>
    );
  }

  const canAnalyse = hasPermission(PERMISSIONS.leadsManage);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Running an analysis is leads:manage (owner, analyst) — a viewer's click would be refused. */}
        {canAnalyse && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={analyse.isPending}
            onClick={() => { setRan(null); analyse.mutate(); }}
          >
            {analyse.isPending ? 'Analysing…' : 'Analyse now'}
          </button>
        )}
        <span className="t-label subtle">
          model {data.model ?? '—'} · runs daily · reading stored results, not live
        </span>
        {ran && <span className="t-label">{ran}</span>}
      </div>

      {data.analyses.length === 0 ? (
        <div className="empty-state t-body">
          No analysis stored yet. {canAnalyse ? <>Use <strong>Analyse now</strong>, or wait</> : 'Wait'} for the daily run.
        </div>
      ) : (
        data.analyses
          // Stable order regardless of which ran most recently, so the panel does not reshuffle
          // itself between visits.
          .slice()
          .sort((a, b) => Object.keys(KIND_LABELS).indexOf(a.kind) - Object.keys(KIND_LABELS).indexOf(b.kind))
          .map((a) => <AnalysisCard key={a.kind} analysis={a} />)
      )}
    </>
  );
}

function AnalysisCard({ analysis }: { analysis: LeadAnalysisItem }) {
  const content = analysis.content;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>
          {KIND_LABELS[analysis.kind] ?? analysis.kind}
        </h2>
        <span className="t-label subtle">
          {analysis.analysis_date} · {formatWhen(analysis.generated_at)} ·{' '}
          {/* Sample size is shown deliberately: a confident recommendation drawn from four leads
              deserves to be read differently from one drawn from four hundred. */}
          {analysis.input_lead_count} leads
        </span>
      </div>
      <div className="card-body">
        {content?.summary && <p className="t-body" style={{ marginTop: 0 }}>{content.summary}</p>}

        {(content?.findings ?? []).map((f, i) => (
          <div
            key={`${f.title}-${i}`}
            style={{
              borderInlineStart: '3px solid var(--border)',
              paddingInlineStart: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {f.title}
              {f.severity && <span className="badge-muted">{f.severity}</span>}
            </div>
            <div className="t-body muted">{f.detail}</div>
            {/* The evidence is what makes a model's claim checkable. Shown, not hidden behind a
                tooltip — an unverifiable recommendation still gets acted on. */}
            {f.evidence && (
              <div className="t-caption subtle" style={{ marginTop: 4 }}>
                Evidence: {f.evidence}
              </div>
            )}
          </div>
        ))}

        {(content?.findings ?? []).length === 0 && !content?.summary && (
          <div className="t-body subtle">The model returned nothing usable for this period.</div>
        )}
      </div>
    </div>
  );
}
